import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../../components/recharts/functions";
import { ReMixChart } from "../../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../../models/chart_model";
import { renderHourTick } from "../../../../../../components/recharts/components";
import { R_CHART_TYPES } from "../../../../const";
import { fetchSimulationProcurementData } from "../../../../../../Rest_api/DemoRestApi";
import loader from "../../../../../../components/recharts/loader.gif";
import { getMonthFromDate } from "../../../../../../extensions/date";

const DataTabs = ["Slot", "Date", "Month"];

const SelectionOptions = [
  { id: "surplus", label: "Surplus", color: "#20B2AA" },
  { id: "deficit", label: "Deficit", color: "#F1935C" },
];

interface SummaryData {
  date: string;
  slot: number;
  market_sale_revenue?: number;
  net_revenue?: number;
  net_procurement_cost?: number;
  market_purchase?: number;
  [key: string]: any;
}

const Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const COST_COLORS = [
  "#5F9EA0",
  "#4682B4",
  "#87CEEB",
  "#20B2AA",
  "#4DD0E1",
  "#00CED1",
  "#48D1CC",
  "#40E0D0",
];

function SurplusSelector({
  selected,
  onSelectionChange,
}: {
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
}) {
  const toggleSelection = (id: string) => {
    if (selected.includes(id)) {
      if (selected.length > 1) {
        onSelectionChange(selected.filter((s) => s !== id));
      } else {
        onSelectionChange(SelectionOptions.map((opt) => opt.id));
      }
    } else {
      onSelectionChange([...selected, id]);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {SelectionOptions.map((option) => {
        const isSelected = selected.includes(option.id);
        return (
          <button
            key={option.id}
            onClick={() => toggleSelection(option.id)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-200 ${
              isSelected
                ? "bg-white shadow-sm border border-gray-200"
                : "bg-transparent hover:bg-gray-100"
            }`}
          >
            <div
              className={`w-3 h-3 rounded transition-all duration-200 ${
                isSelected ? "opacity-100" : "opacity-40"
              }`}
              style={{ backgroundColor: option.color }}
            />
            <span
              className={`font-medium text-sm transition-all duration-200 ${
                isSelected ? "text-gray-700" : "text-gray-500"
              }`}
            >
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function SimulationCostAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  setUniqueTargetDate,
  selectedTargetDate,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  setUniqueTargetDate?: any;
  selectedTargetDate: any[];
}) {
  const [data, setData] = useState<SummaryData[]>([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>(DataTabs[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [allCostFields, setAllCostFields] = useState<string[]>([]);
  const [selectedChartType] = useState(R_CHART_TYPES[1]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([
    "surplus",
    "deficit",
  ]);

  useEffect(() => {
    const tabIndex = DataTabs.indexOf(activeTab);
    if (tabIndex !== -1) {
      setSelectedInterval(Intervals[tabIndex]);
    }
  }, [activeTab]);

  const detectAllCostFields = useCallback((dataArray: any[]) => {
    if (!dataArray || dataArray.length === 0) return [];

    const costSet = new Set<number>();

    dataArray.forEach((item) => {
      Object.keys(item).forEach((key) => {
        const match = key.match(/^cost(\d+)$/);
        if (match) {
          costSet.add(parseInt(match[1]));
        }
      });
    });

    const sortedCosts = Array.from(costSet)
      .sort((a, b) => a - b)
      .map((num) => `cost${num}`);
    return sortedCosts;
  }, []);

  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchSimulationProcurementData();
      let dataArray = Array.isArray(res) ? res : res?.data || [];

      if (!Array.isArray(dataArray)) {
        console.error("Data is not an array:", dataArray);
        setError?.("Invalid data format: expected array.");
        setAllCostFields([]);
        setData([]);
        return;
      }

      const detectedCosts = detectAllCostFields(dataArray);
      setAllCostFields(detectedCosts);

      if (setUniqueTargetDate && dataArray.length > 0) {
        const uniqueDates = Array.from(
          new Set(
            dataArray
              .filter((item) => item.date)
              .map((item: { date: string }) =>
                item.date.split("-").reverse().join("-")
              )
          )
        );
        setUniqueTargetDate(uniqueDates);
      }

      setData(dataArray);
    } catch (error) {
      console.error("Error fetching data", error);
      setError?.("An error occurred while fetching data.");
      setAllCostFields([]);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [setError, setUniqueTargetDate, detectAllCostFields]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      return "slot";
    }
    return selectedInterval.key;
  }, [selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  useEffect(() => {
    if (!data || data.length === 0) {
      setChartData([]);
      setChartKeys([]);
      return;
    }

    let filteredData = data.map((item) => {
      const cleanedItem: any = {
        ...item,
        month: getMonthFromDate(item.date),
      };

      Object.keys(cleanedItem).forEach((key) => {
        const value = cleanedItem[key];
        if (typeof value === "number" && isNaN(value)) {
          cleanedItem[key] = 0;
        } else if (
          typeof value === "string" &&
          (value === "NaN" || value.toLowerCase() === "nan")
        ) {
          cleanedItem[key] = 0;
        } else if (value === null || value === undefined) {
          if (
            key.startsWith("cost") ||
            key.includes("market") ||
            key.includes("procurement") ||
            key.includes("revenue")
          ) {
            cleanedItem[key] = 0;
          }
        }
      });

      return cleanedItem;
    });

    if (selectedTargetDate && selectedTargetDate.length > 0) {
      const types = selectedTargetDate.map(
        (t: any) => t.value?.split("-").reverse().join("-") ?? t
      );
      filteredData = filteredData.filter((row) => types.includes(row.date));
    }

    function getKeys() {
      if (selectedInterval.key === "time") {
        return ["slot", "date"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      }
      return [selectedInterval.values[0]];
    }

    const aggregationKeys = [
      "market_sale_revenue",
      "net_revenue",
      "net_procurement_cost",
      "market_purchase",
    ];

    allCostFields.forEach((cost) => {
      aggregationKeys.push(cost);
    });

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["slot", "date", "month"],
      groupBy: [""],
      volumeKey: "",
      aggregationKeys: aggregationKeys,
      averageKeys: [],
      averageDuplicationKeys: [],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);

    // Remove zero values completely - convert to undefined
    const cleanedChartData = cData.data.map((item: any) => {
      const cleaned = { ...item };
      Object.keys(cleaned).forEach((key) => {
        // Skip non-numeric keys (slot, date, month, etc.)
        if (key === 'slot' || key === 'date' || key === 'month') {
          return;
        }
        
        const value = cleaned[key];
        if (typeof value === "number" && isNaN(value)) {
          cleaned[key] = undefined;
        } else if (
          typeof value === "string" &&
          (value === "NaN" || value.toLowerCase() === "nan")
        ) {
          cleaned[key] = undefined;
        } else if (typeof value === "number" && value === 0) {
          // Set zero values to undefined so they don't render in the chart
          cleaned[key] = undefined;
        } else if (value === null) {
          cleaned[key] = undefined;
        }
      });
      return cleaned;
    });
    
    setChartData(cleanedChartData);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedBlock,
    selectedInterval,
    selectedTargetDate,
    allCostFields,
  ]);

  const activeCostFields = useMemo(() => {
    if (
      !chartData ||
      chartData.length === 0 ||
      !allCostFields ||
      allCostFields.length === 0
    ) {
      return [];
    }

    const activeCosts: string[] = [];

    allCostFields.forEach((costField) => {
      const hasCostData = chartData.some((item) => {
        const costKeys = Object.keys(item).filter(
          (key) => key === costField || key.endsWith(`_${costField}`)
        );

        return costKeys.some((key) => {
          const value = item[key];
          const numValue =
            typeof value === "string" ? parseFloat(value) : value;
          return (
            numValue !== null &&
            numValue !== undefined &&
            numValue > 0 &&
            !isNaN(numValue) &&
            isFinite(numValue)
          );
        });
      });

      if (hasCostData) {
        activeCosts.push(costField);
      }
    });
    return activeCosts;
  }, [chartData, allCostFields]);

  // Helper function to check if a specific dataKey has any non-zero/non-null values
  const hasNonZeroValuesForDataKey = useCallback(
    (dataKey: string) => {
      if (!chartData || chartData.length === 0) return false;

      return chartData.some((item) => {
        const value = item[dataKey];
        if (value === null || value === undefined) return false;
        
        const numValue =
          typeof value === "string" ? parseFloat(value) : value;
        return (
          numValue !== 0 &&
          !isNaN(numValue) &&
          isFinite(numValue) &&
          numValue > 0
        );
      });
    },
    [chartData]
  );

  const chartSuffixes = useMemo(() => {
    const metrics = [];

    // Add Multiple Offer Costs stacked together (teal shades)
    if (activeCostFields.length > 0) {
      activeCostFields.forEach((costField, index) => {
        const costNum = costField.replace("cost", "");
        metrics.push({
          suffix: costField,
          displayName: `Offer ${costNum} Cost`,
          stackSuffix: "stack1",
          stroke: COST_COLORS[index % COST_COLORS.length],
          fill: COST_COLORS[index % COST_COLORS.length],
          chartType: ChartType.Bar,
          unit: "(Lakh) ₹",
          yAxisId: "left",
        });
      });
    }

    // Add Net Revenue - only if surplus is selected
    if (selectedOptions.includes("surplus")) {
      metrics.push({
        suffix: "net_revenue",
        displayName: "Net Revenue",
        stackSuffix: "stack1",
        stroke: "#F1935C",
        fill: "#F1935C",
        chartType: ChartType.Bar,
        unit: "(Lakh) ₹",
        yAxisId: "left",
      });
    }

    // Add Market Purchase Cost (DAM) - only if deficit is selected
    if (selectedOptions.includes("deficit")) {
      metrics.push({
        suffix: "market_purchase",
        displayName: "Market Purchase Cost (DAM)",
        stackSuffix: "stack1",
        stroke: "#34656D",
        fill: "#34656D",
        chartType: ChartType.Bar,
        unit: "(Lakh) ₹",
        yAxisId: "left",
      });
    }

    // Add Market Sale Revenue (DAM) - only if surplus is selected
    if (selectedOptions.includes("surplus")) {
      metrics.push({
        suffix: "market_sale_revenue",
        displayName: "Market Sale Revenue (DAM)",
        stackSuffix: "stack2",
        stroke: "#49B3FF",
        fill: "#49B3FF",
        chartType: ChartType.Bar,
        unit: "(Lakh) ₹",
        yAxisId: "left",
      });
    }

    // Add Net Procurement Cost - only if deficit is selected
    if (selectedOptions.includes("deficit")) {
      metrics.push({
        suffix: "net_procurement_cost",
        displayName: "Net Procurement Cost",
        stackSuffix: "stack2",
        stroke: "#C10404",
        fill: "#C10404",
        chartType: ChartType.Bar,
        unit: "(Lakh) ₹",
        yAxisId: "left",
      });
    }

    return metrics;
  }, [activeCostFields, selectedOptions]);

  const createChartObject = useCallback(
    (
      key: string,
      suffix: string,
      displayName: string,
      stackSuffix: string,
      chartType: ChartType,
      unit: string,
      stroke: string,
      fill: string,
      yAxisId: string
    ) => {
      const dataKey = key ? `${key}_${suffix}` : suffix;
      return {
        dataKey: dataKey,
        name: displayName,
        legendColor: fill === "none" ? stroke : fill,
        type: chartType,
        unit: unit,
        stroke: stroke,
        fill: fill,
        yAxisId: yAxisId,
        stackId: stackSuffix || undefined,
      };
    },
    []
  );

  const legends = useMemo(() => {
    if (!chartKeys || chartKeys.length === 0 || chartSuffixes.length === 0) {
      return [];
    }

    const result: any[] = [];

    chartSuffixes.forEach(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        yAxisId,
        stroke,
        fill,
      }) => {
        chartKeys.forEach((key) => {
          const chartObj = createChartObject(
            key,
            suffix,
            displayName,
            stackSuffix,
            chartType,
            unit,
            stroke,
            fill || stroke,
            yAxisId
          );

          // Only add to legends if the dataKey has non-zero values
          if (hasNonZeroValuesForDataKey(chartObj.dataKey)) {
            result.push(chartObj);
          }
        });
      }
    );

    return result;
  }, [chartKeys, chartSuffixes, createChartObject, hasNonZeroValuesForDataKey]);

  function secondDataKey() {
    if (selectedInterval.isTime && selectedBlock.length > 0) {
      return "date";
    }
    if (selectedInterval.isTime) {
      return "date";
    }
    return undefined;
  }

  // Get label text for active cost fields
  const getCostFieldsLabel = () => {
    if (activeCostFields.length === 0) return "";
    if (activeCostFields.length === 1) return "1 Offer Cost";
    return `${activeCostFields.length} Offer Costs`;
  };

  return (
    <div className="dash-card !border !border-[#DFEAF2] bg-white w-full !min-h-[100%] rounded-2xl p-4">
      <div className="flex justify-between items-center border-b border-[#DFEAF2] pb-3 mb-4">
        <div className="flex items-center gap-3">
          <h2 className="chartHeading !text-[#34656D]">
            System Finances (Revenue/Cost)
          </h2>
          {activeCostFields.length > 0 && (
            <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">
              {getCostFieldsLabel()}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <SurplusSelector
            selected={selectedOptions}
            onSelectionChange={setSelectedOptions}
          />
        </div>
      </div>

      {loading ? (
        <div className="w-full bg-white flex items-center justify-center min-h-[400px]">
          <img src={loader} width={200} alt="loader" className="block" />
        </div>
      ) : chartData.length === 0 ? (
        <div className="w-full bg-white flex items-center justify-center min-h-[400px]">
          <p className="text-gray-500">No data available</p>
        </div>
      ) : (
        <ReMixChart
          chartHeight="calc(100% - 70px)"
          data={chartData}
          showBrush={chartData.length > 96 ? true : undefined}
          brushStart={BrushStart.Start}
          isTimeSlot={selectedInterval.isTime}
          onlyTitle={true}
          legends={legends}
          yAxisLabel="Lakh (₹)"
          nameGap={40}
          xDataKey={xDataKey}
          top={10}
          bottom={30}
          xTick={
            selectedInterval.isTime && selectedBlock.length === 0
              ? (value: any, index: any) => renderHourTick(value, 10)
              : undefined
          }
          secondXDataKey={secondDataKey()}
          showToolbox={false}
        />
      )}
    </div>
  );
}